import { ViewConfig } from '../../../models/view-config.interface';
import { CarrierFoldersPaginatedService } from '@app/carrier/services/carrier-folders-paginated.service';
import { DeliveryNoteBundlesService } from 'api/services';
import { AppStore } from '@app/stores/appStore';
import { CarrierViewActionsService } from '../carrier-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { lastValueFrom } from 'rxjs';
import { ColumnCreator, ColumnValue } from '../column-creator';
import {
    FolderData,
    DeliveryNoteData,
    TableRowData,
    hasKey,
    hasStatus,
    hasReadOnly,
    hasDateReceived,
    hasNumberOfDeliveryNotes,
    isFolderData,
    isDeliveryNoteData,
    DeliveryNote,
    hasDeliveryNoteBundleStatus,
} from '@app/shared/models/table-data-types';
import { TourPlanStore } from '@app/stores/tourPlanStore';

export interface CarrierFoldersConfigDeps {
    carrierFoldersPaginatedService: CarrierFoldersPaginatedService;
    deliveryNoteBundlesService: DeliveryNoteBundlesService;
    actionsService: CarrierViewActionsService;
    appStore: InstanceType<typeof AppStore>;
    tourPlanStore: InstanceType<typeof TourPlanStore>;
}

export class CarrierFoldersConfig {
    static create(deps: CarrierFoldersConfigDeps): ViewConfig {
        return {
            id: 'carrier-folders',
            name: 'Folders',
            translationKey: 'OUTGOING-GOODS.TOGGLER.FOLDERS',
            type: 'folders',
            userRole: 'carrier',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey = deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve([]);
                    }
                    return deps.carrierFoldersPaginatedService.loadCarrierFoldersPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({
                        visible: context => deps.appStore.isTourPlanMode(),
                        selectableCondition: (item: TableRowData) => {
                            // Read signal to trigger OnPush change detection when tour plan changes
                            deps.tourPlanStore.tourPlanVersion();
                            if (!isFolderData(item)) return false;
                            const tourPlanNotes =
                                deps.tourPlanStore.tourPlanDeliveryNotes();
                            const notesInTourForBundle = tourPlanNotes.filter(
                                note => note.deliveryNoteBundleKey === item._key
                            ).length;
                            return (
                                notesInTourForBundle <
                                item.numberOfDeliveryNotes
                            );
                        },
                    }),
                    ColumnCreator.details(),
                    ColumnCreator.bundleNumber(),
                    ColumnCreator.bundleName(),
                    ColumnCreator.actions({
                        width: '200px',
                        configuration: {
                            type: 'bundle',
                            showDeliveryNotesCount: true,
                            showView: true,
                            showDownload: true,
                            showWalletInvite: true,

                            // Endpoints
                            viewEndpoint: (item: TableRowData) => {
                                if (isFolderData(item)) {
                                    return deps.actionsService.viewBundleDetails(
                                        item
                                    );
                                }
                                return undefined;
                            },
                            downloadEndpoint: (item: TableRowData) => {
                                if (isFolderData(item)) {
                                    return deps.actionsService.downloadDeliveryBundle(
                                        item
                                    );
                                }
                                return undefined;
                            },
                            walletInviteEndpoint: (item: TableRowData) => {
                                if (isFolderData(item)) {
                                    return deps.actionsService.sendWalletInvite(
                                        item
                                    );
                                }
                                return undefined;
                            },

                            // Context
                            context: 'carrier',

                            // Custom visibility conditions
                            isWalletInviteVisible: (item: TableRowData) => {
                                const isSent =
                                    hasStatus(item) && item.status === 'sent';
                                const isNotReadOnly =
                                    !hasReadOnly(item) || !item.read_only;
                                return isSent && isNotReadOnly;
                            },
                            isDownloadVisible: (item: TableRowData) => {
                                return (
                                    hasNumberOfDeliveryNotes(item) &&
                                    item.numberOfDeliveryNotes > 0
                                );
                            },
                        } as TableActionsConfig,
                    }),
                    ColumnCreator.consignorName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CONSIGNOR',
                    }),
                    ColumnCreator.carrierName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CARRIER',
                    }),
                    ColumnCreator.consigneeName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CONSIGNEE',
                    }),
                    ColumnCreator.dateShipped({
                        header: 'SPEDITEUR.TABLE.HEADERS.SENT-DATE',
                        filterable: true,
                    }),
                    ColumnCreator.status({
                        header: 'SPEDITEUR.TABLE.HEADERS.STATUS',
                        showDiscrepanciesIcon: true,
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: true,
                    expandable: true,
                },
                expandedRowConfig: {
                    expandableCondition: (item: TableRowData) => {
                        return (
                            hasNumberOfDeliveryNotes(item) &&
                            item.numberOfDeliveryNotes > 0
                        );
                    },
                    tableConfig: {
                        columns: [
                            ColumnCreator.actions({
                                width: '150px',
                                configuration: (
                                    row: TableRowData,
                                    value: ColumnValue,
                                    parentRow?: TableRowData
                                ) =>
                                    ({
                                        type: 'deliveryNote',
                                        showView: true,
                                        showDownload: true,

                                        // Endpoints
                                        viewEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) => {
                                            if (
                                                isDeliveryNoteData(item) &&
                                                (!parentRow ||
                                                    isFolderData(parentRow))
                                            ) {
                                                return deps.actionsService.viewDeliveryNote(
                                                    item as DeliveryNoteData,
                                                    parentRow as FolderData
                                                );
                                            }
                                            return undefined;
                                        },
                                        downloadEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) => {
                                            if (
                                                isDeliveryNoteData(item) &&
                                                (!parentRow ||
                                                    isFolderData(parentRow))
                                            ) {
                                                return deps.actionsService.downloadDeliveryNote(
                                                    item as DeliveryNoteData,
                                                    parentRow as FolderData
                                                );
                                            }
                                            return undefined;
                                        },

                                        // Configuration
                                        organizationSiteKey:
                                            parentRow?.orgSiteKey,
                                        bundleKey:
                                            parentRow && hasKey(parentRow)
                                                ? parentRow._key
                                                : undefined,
                                        bundleStatus:
                                            parentRow && hasStatus(parentRow)
                                                ? parentRow.status
                                                : undefined,
                                        context: 'carrier',

                                        // Visibility conditions - carriers can only view/download
                                        isEditVisible: () => false,
                                        isDeleteVisible: () => false,
                                    }) as TableActionsConfig,
                            }),
                            ColumnCreator.deliveryNoteNumber(),
                            ColumnCreator.orderNumber(),
                            ColumnCreator.referenceNumber({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NO',
                                orgType: 'carrier',
                                readOnly: (row, parentRow) => {
                                    const parentClosed =
                                        parentRow &&
                                        hasStatus(parentRow) &&
                                        parentRow.status === 'closed';
                                    const rowReadOnly =
                                        hasReadOnly(row) && row.read_only;
                                    const rowReceived =
                                        hasDateReceived(row) &&
                                        !!row.dateReceived;
                                    const deliveryNoteClosed =
                                        hasStatus(row) &&
                                        row.status === 'closed';
                                    const bundleStatusClosed =
                                        hasDeliveryNoteBundleStatus(row) &&
                                        row.deliveryNoteBundleStatus ===
                                            'closed';
                                    return (
                                        parentClosed ||
                                        rowReadOnly ||
                                        rowReceived ||
                                        deliveryNoteClosed ||
                                        bundleStatusClosed
                                    );
                                },
                            }),
                            ColumnCreator.dateAnnounced(),
                            ColumnCreator.dateReceived(),
                            ColumnCreator.carrierLicensePlate({
                                plateType: 'incoming',
                            }),
                            ColumnCreator.carrierLicensePlate({
                                plateType: 'outgoing',
                            }),
                            ColumnCreator.status({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE',
                            }),
                            ColumnCreator.discrepancies(),
                            ColumnCreator.palletTickets(),
                            ColumnCreator.originalConsignor(),
                            ColumnCreator.originalConsignorGln(),
                        ],
                        apiConfig: {
                            loadData: async (
                                parentRow: TableRowData,
                                organizationSiteKey: string,
                                customParams?: Record<string, unknown>
                            ) => {
                                if (!isFolderData(parentRow)) return [];

                                const result =
                                    deps.deliveryNoteBundlesService.getCarrierBundle(
                                        {
                                            deliveryNoteBundleKey:
                                                parentRow._key,
                                            organizationSiteKey:
                                                parentRow.carrierKey,
                                        }
                                    );
                                const bundle =
                                    result instanceof Promise
                                        ? await result
                                        : await lastValueFrom(result);

                                // Transform delivery notes to include required properties
                                return (bundle.deliveryNotes || []).map(
                                    (deliveryNote: DeliveryNote) => ({
                                        ...deliveryNote,
                                        orgSiteKey: parentRow.orgSiteKey,
                                        read_only:
                                            (hasStatus(parentRow) &&
                                                parentRow.status ===
                                                    'closed') ||
                                            (hasReadOnly(parentRow) &&
                                                parentRow.read_only) ||
                                            false,
                                        status:
                                            deliveryNote.dateReceived &&
                                            deliveryNote.dateReceived > 0
                                                ? 'closed'
                                                : bundle.status,
                                    })
                                );
                            },
                            organizationSiteKeyResolver: (
                                row: TableRowData
                            ) => {
                                if (isFolderData(row)) {
                                    return row.carrierKey;
                                }
                                return '';
                            },
                            customParams: (row: TableRowData) => {
                                if (isFolderData(row)) {
                                    return {
                                        bundleKey: row._key,
                                        bundleStatus: row.status,
                                    };
                                }
                                return {};
                            },
                        },
                        loadingMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.LOADING-DELIVERY-NOTES',
                        emptyMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.NO-DELIVERY-NOTES',
                        tableClass: 'expanded-delivery-notes-table',
                        maxHeight: '400px',
                    },
                },
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'bundleNumber',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                    },
                    {
                        keyName: 'name',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.BUNDLE-NAME',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.BUNDLE-NAME',
                    },
                    {
                        keyName: 'consignorName',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
                    },
                    {
                        keyName: 'carrierName',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CARRIER-NAME',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CARRIER-NAME',
                    },
                    {
                        keyName: 'consigneeName',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CONSIGNEE-NAME',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CONSIGNEE-NAME',
                    },
                    {
                        keyName: 'checkoutDate',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
                    },
                ],
                showClosed: false,
                enableAdvancedFilters: false,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'name',
                    'bundleNumber',
                    'status',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'checkoutDate',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'sharePermissions',
                        label: 'Share Permissions',
                        translationKey:
                            'SPEDITEUR.TABLE.ACTIONS.SHARE-PERMISSIONS',
                        icon: 'ios_share',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 1,
                        visible: (item, selection) => !deps.appStore.isTourPlanMode(),
                        disabled: (item, selection) => false,
                        handler: async (item, selection) => {
                            const organizationSite =
                                deps.appStore.currentOrganizationSite();
                            if (organizationSite) {
                                await deps.actionsService.sharePermissions(
                                    selection || [],
                                    organizationSite
                                );
                            }
                        },
                    },
                    {
                        id: 'planTour',
                        label: 'Plan Tour',
                        translationKey: 'SPEDITEUR.TABLE.ACTIONS.PLAN-TOUR',
                        icon: 'qr_code_scanner',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) => !deps.appStore.isTourPlanMode(),
                        disabled: (item, selection) => false,
                        handler: async (item, selection) => {
                            await deps.actionsService.planTour(selection || []);
                        },
                    },
                ],
            },
            permissions: {
                canCreate: true,
                canUpdate: true,
                canDelete: true,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
        };
    }
}
